// All Phases board — card quick view (Luma/FLS item 1). Lives on the
// board's root element so cards inside the lazily-loaded phase
// turbo-frames reach it through the ancestor scope; the slide-over
// panel is rendered once next to the frames and filled from the
// /board_cards/:project_id JSON on every open.
//
// Everything is AJAX (no reloads): checklist ticks, comments,
// presign → S3 PUT → register attachments — and after each change the
// matching card's little counters are updated in place.

import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

export default class extends Controller {
  static targets = ["secondaryPhaseWrap", "secondaryPhaseSelect", 
    "modal", "title", "projectLink", "infoRow", "body", "loading",
    "descriptionSection", "descriptionText", "descriptionInput",
    "descriptionEditButton", "descriptionSaveButton", "descriptionCancelButton",
    "checklistSection", "checklistCount", "checklistList", "checklistNewInput", "templateLink",
    "labelsSection", "labelsChips", "labelSearchInput", "labelsDropdown",
    "labelEditor", "labelSwatches", "labelNameInput", "labelDeleteButton",
    "checklistBar",
    "commentsSection", "commentInput", "commentsList",
    "attachmentsSection", "dropzone", "fileInput", "attachmentsList"
  ]
  static values = {
    showUrl: String,
    csrfToken: String
  }

  connect() {
    this._projectId = null
    this._data = null
    this._boundEscape = (e) => { if (e.key === "Escape") this.close() }
  }

  disconnect() {
    document.removeEventListener("keydown", this._boundEscape)
    if (this._boundLabelOutside) document.removeEventListener("mousedown", this._boundLabelOutside)
    if (this._checklistSortable) {
      this._checklistSortable.destroy()
      this._checklistSortable = null
    }
  }

  // ── Open / close ─────────────────────────────────────────────────

  // Card icons deep-link into their popup section; stopPropagation so
  // the card's own open doesn't fire twice.
  openSection(event) {
    event.stopPropagation()
    this.open(event)
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  async open(event) {
    // The favourite star sits inside the card button — let it do its
    // thing without opening the popup.
    if (event.target.closest('[data-controller="favorite"]')) return
    event.preventDefault()

    const trigger = event.currentTarget
    this._projectId = trigger.dataset.boardCardProjectId
    this._focusSection = trigger.dataset.boardCardSection || null
    if (!this._projectId) return

    this.modalTarget.classList.remove("hidden")
    document.addEventListener("keydown", this._boundEscape)

    this._showLoading(true)
    this._hideSections()
    this.titleTarget.textContent = ""
    this.infoRowTarget.innerHTML = ""

    try {
      const res = await fetch(this._url(this.showUrlValue), {
        headers: { "Accept": "application/json" }, credentials: "same-origin"
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      this._data = await res.json()
      this._render()
    } catch (err) {
      this._showLoading(false)
      this.titleTarget.textContent = "—"
      console.warn("[board-card] load failed", err)
    }
  }

  close(event) {
    event?.preventDefault()
    this.modalTarget.classList.add("hidden")
    document.removeEventListener("keydown", this._boundEscape)
    this._hideLabelDropdown()
    if (this.hasLabelSearchInputTarget) this.labelSearchInputTarget.value = ""
    if (this._checklistSortable) {
      this._checklistSortable.destroy()
      this._checklistSortable = null
    }
  }

  // ── Render ───────────────────────────────────────────────────────

  _render() {
    const d = this._data
    this._showLoading(false)

    this.titleTarget.textContent = d.project_name
    this.projectLinkTarget.setAttribute("href", d.project_path)
    this._renderInfoRow()
    this._renderSecondaryPhase()

    // Description — always shown; editable for admins.
    this.descriptionSectionTarget.classList.remove("hidden")
    this._renderDescription()

    if (d.features.labels) {
      this.labelsSectionTarget.classList.remove("hidden")
      this._renderLabels(d.labels || [])
    }
    if (d.features.checklist) {
      this.checklistSectionTarget.classList.remove("hidden")
      this._renderChecklist(d.checklist || [])
      if (this.hasTemplateLinkTarget) {
        const show = !!(d.can_edit_template && d.template_settings_path)
        this.templateLinkTarget.classList.toggle("hidden", !show)
        if (show) this.templateLinkTarget.setAttribute("href", d.template_settings_path)
      }
    }
    if (d.features.comments) {
      this.commentsSectionTarget.classList.remove("hidden")
      this._renderComments(d.comments || [])
    }
    if (d.features.attachments) {
      this.attachmentsSectionTarget.classList.remove("hidden")
      this._renderAttachments(d.attachments || [])
    }

    this._focusRequestedSection()
  }

  // Start / due / cost / variance / phase under the title, as quiet
  // little pills (Phelipe 2026-08-26 restyle) — the money only reaches
  // people cleared for Portfolio figures.
  // Secondary phase picker (Phelipe 2026-09-03): the steps of this
  // project's primary phase, "none" first. Hidden when there are none.
  _renderSecondaryPhase() {
    if (!this.hasSecondaryPhaseWrapTarget) return
    const d = this._data
    const options = d.secondary_phase_options || []
    this.secondaryPhaseWrapTarget.classList.toggle("hidden", options.length === 0)
    if (options.length === 0) return
    // No blank choice: a project always sits in a step of its phase — the
    // first one until someone picks otherwise (Phelipe 2026-09-03).
    const select = this.secondaryPhaseSelectTarget
    select.innerHTML = ""
    options.forEach((o) => {
      const opt = document.createElement("option")
      opt.value = String(o.id)
      opt.textContent = o.name
      select.appendChild(opt)
    })
    select.value = d.secondary_phase ? String(d.secondary_phase.id) : String(options[0].id)
  }

  async changeSecondaryPhase(event) {
    const select = event.currentTarget
    const url = `/board_cards/${this._data.project_id}/secondary_phase`
    try {
      const data = await this._json("PATCH", url, { board_secondary_phase_id: select.value })
      if (data && data.error) throw new Error(data.error)
      this._data.secondary_phase = data.secondary_phase
      this._paintSecondaryChip(data.secondary_phase)
      this._flash(this.element.dataset.boardCardSavedLabel || "Saved")
    } catch (err) {
      this._flash(err.message || "Couldn't save", "alert")
      this._renderSecondaryPhase()
    }
  }

  // Keep the card face in step without a reload (house rule): the chip in
  // the label row names the step when the board is grouped by primary phase.
  _paintSecondaryChip(step) {
    const chip = document.querySelector(`[data-secondary-chip-for="${this._data.project_id}"]`)
    if (!chip) return
    chip.className = "badge " + (step ? step.chip_class : "hidden")
    chip.textContent = step ? step.name : ""
  }

  _flash(message, type = "notice") {
    const div = document.createElement("div")
    div.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    div.textContent = message
    div.setAttribute("data-controller", "flash")
    div.setAttribute("data-flash-dismiss-after-value", "5000")
    document.body.prepend(div)
  }

  _renderInfoRow() {
    const d = this._data
    this.infoRowTarget.innerHTML = ""
    const add = (label, value) => {
      if (!value) return
      const pill = document.createElement("span")
      pill.className = "inline-flex items-baseline gap-1 rounded-md bg-gray-50 border border-gray-100 px-2 py-0.5 whitespace-nowrap"
      const key = document.createElement("span")
      key.className = "text-[11px] uppercase tracking-wide text-gray-400"
      key.textContent = label
      const val = document.createElement("span")
      val.className = "text-[13px] text-gray-700"
      val.textContent = value
      pill.appendChild(key)
      pill.appendChild(val)
      this.infoRowTarget.appendChild(pill)
    }
    add(this.element.dataset.boardCardStartLabel || "Start", d.start_on)
    add(this.element.dataset.boardCardDueLabel || "Due", d.due_on)
    add(this.element.dataset.boardCardCostLabel || "Cost", d.cost)
    add(this.element.dataset.boardCardVarianceLabel || "Variance", d.variance)

    // Phase timing (Luma 2026-08-26): "Design · 15.9 wks in · avg …" —
    // parts the server couldn't compute are simply left out.
    const line = d.phase_line
    if (line && line.phase_label) {
      const parts = [`${line.in_weeks} wks in`]
      if (line.avg_weeks) parts.push(`avg ${line.avg_weeks} wks`)
      add("Phase", `${line.phase_label} · ${parts.join(" · ")}`)
    }
  }

  // Icon deep-link: scroll the requested section into view with a
  // brief highlight so the user lands exactly where they clicked.
  _focusRequestedSection() {
    const section = this._focusSection
    this._focusSection = null
    if (!section) return

    // element, not bodyTarget: the Task chat pane sits beside the body now.
    const el = this.element.querySelector(`[data-board-card-section-name="${section}"]`)
    if (!el || el.classList.contains("hidden")) return

    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: "smooth", block: "start" })
      el.classList.add("ring-2", "ring-brand", "ring-offset-4", "rounded-lg")
      setTimeout(() => el.classList.remove("ring-2", "ring-brand", "ring-offset-4", "rounded-lg"), 1600)
    })
  }

  _renderDescription() {
    const d = this._data
    const empty = !(d.description || "").trim()
    // Empty + editable reads as one quiet invitation instead of a bare
    // dash with an Edit link dangling under it (Phelipe 2026-08-26).
    this.descriptionTextTarget.textContent = empty ? (d.can_edit_description ? "" : "—") : d.description
    this.descriptionTextTarget.classList.toggle("hidden", empty && d.can_edit_description)
    this.descriptionInputTarget.classList.add("hidden")
    this.descriptionSaveButtonTarget.classList.add("hidden")
    this.descriptionCancelButtonTarget.classList.add("hidden")
    this.descriptionEditButtonTarget.textContent = empty ? "Add a description…" : "Edit"
    this.descriptionEditButtonTarget.classList.toggle("hidden", !d.can_edit_description)
  }

  // ── Labels — Planner-style combobox (Phelipe 2026-08-26) ──────────
  // Assigned chips with × up top; below, a search box whose dropdown
  // lists every label (pencil to edit) and offers "+ Create" when the
  // typed name doesn't exist yet.
  _renderLabels(labels) {
    this._labels = labels
    const assigned = labels.filter(l => l.assigned)

    this.labelsChipsTarget.innerHTML = ""

    // The card's own chips — region, secondary phase — sit here with the
    // company's labels and come off this one card with the × (Phelipe
    // 2026-09-03). A hidden one stays listed, greyed, with a + to bring
    // it back, so it is never lost.
    const defaults = (this._data && this._data.default_chips) || []
    defaults.forEach((c) => {
      const chip = document.createElement("span")
      chip.dataset.defaultChip = c.key
      if (c.hidden) {
        chip.className = "badge bg-gray-100 text-gray-400 inline-flex items-center gap-1 line-through"
        chip.appendChild(document.createTextNode(c.name))
        const plus = document.createElement("button")
        plus.type = "button"
        plus.className = "cursor-pointer no-underline opacity-70 hover:opacity-100 leading-none"
        plus.title = this.element.dataset.boardCardShowChipLabel || "Show on card"
        plus.textContent = "+"
        plus.addEventListener("click", () => this.toggleDefaultChip(c.key, false))
        chip.appendChild(plus)
      } else {
        chip.className = `badge ${c.chip_class} inline-flex items-center gap-1`
        chip.appendChild(document.createTextNode(c.name))
        const x = document.createElement("button")
        x.type = "button"
        x.className = "cursor-pointer opacity-60 hover:opacity-100 leading-none"
        x.title = this.element.dataset.boardCardHideChipLabel || "Hide on card"
        x.textContent = "×"
        x.addEventListener("click", () => this.toggleDefaultChip(c.key, true))
        chip.appendChild(x)
      }
      this.labelsChipsTarget.appendChild(chip)
    })

    assigned.forEach(label => {
      const chip = document.createElement("span")
      chip.className = `badge ${label.chip_class} inline-flex items-center gap-1`
      chip.appendChild(document.createTextNode(label.name))

      const x = document.createElement("button")
      x.type = "button"
      x.className = "cursor-pointer opacity-60 hover:opacity-100 leading-none"
      x.title = "Remove"
      x.textContent = "×"
      x.addEventListener("click", () => this._toggleLabelId(label.id))
      chip.appendChild(x)
      this.labelsChipsTarget.appendChild(chip)
    })
    this.labelsChipsTarget.classList.toggle("hidden", assigned.length === 0 && defaults.length === 0)

    if (this._labelsDropdownOpen) this._renderLabelDropdown()
  }

  async toggleDefaultChip(key, hidden) {
    const data = await this._json("PATCH", `/board_cards/${this._data.project_id}/default_chip`, { key, hidden })
    if (!data || data.error) return
    this._data.default_chips = data.default_chips || []
    this._renderLabels(this._labels || [])
    this._syncDefaultChipOnCard(key, hidden)
  }

  // Keep the card face in step without a reload (house rule): the region
  // chip in the label row, or the primary-phase badge at the bottom (shown
  // when the board is grouped by secondary phase).
  _syncDefaultChipOnCard(key, hidden) {
    const id = this._data.project_id
    const sel = key === "region" ? `[data-region-chip-for="${id}"]` : `[data-primary-phase-for="${id}"]`
    const chip = document.querySelector(sel)
    if (chip) chip.classList.toggle("hidden", hidden)
  }

  labelSearch() {
    this._openLabelDropdown()
    this._renderLabelDropdown()
  }

  labelSearchKeydown(event) {
    if (event.key === "Escape") {
      // Close just the dropdown — not the whole popup.
      event.stopPropagation()
      this._hideLabelDropdown()
      this.labelSearchInputTarget.blur()
    } else if (event.key === "Enter") {
      event.preventDefault()
      const query = this.labelSearchInputTarget.value.trim()
      if (!query) return
      const exact = (this._labels || []).find(l => l.name.toLowerCase() === query.toLowerCase())
      if (exact) {
        this._toggleLabelId(exact.id)
        this.labelSearchInputTarget.value = ""
        this._renderLabelDropdown()
      } else {
        this._startLabelCreate(query)
      }
    }
  }

  _openLabelDropdown() {
    if (this._labelsDropdownOpen) return
    this._labelsDropdownOpen = true
    this.labelsDropdownTarget.classList.remove("hidden")
    if (!this._boundLabelOutside) {
      this._boundLabelOutside = (e) => {
        if (!this.labelsSectionTarget.contains(e.target)) this._hideLabelDropdown()
      }
    }
    document.addEventListener("mousedown", this._boundLabelOutside)
  }

  _hideLabelDropdown() {
    this._labelsDropdownOpen = false
    if (this.hasLabelsDropdownTarget) this.labelsDropdownTarget.classList.add("hidden")
    if (this._boundLabelOutside) document.removeEventListener("mousedown", this._boundLabelOutside)
  }

  _renderLabelDropdown() {
    const raw = this.labelSearchInputTarget.value.trim()
    const query = raw.toLowerCase()
    const matches = (this._labels || []).filter(l => !query || l.name.toLowerCase().includes(query))
    const dd = this.labelsDropdownTarget
    dd.innerHTML = ""

    matches.forEach(label => {
      const row = document.createElement("div")
      row.className = "flex items-center gap-2 px-2.5 py-1.5 hover:bg-gray-50 cursor-pointer"
      row.addEventListener("click", () => this._toggleLabelId(label.id))

      const chip = document.createElement("span")
      chip.className = `badge ${label.chip_class}`
      chip.textContent = label.name
      row.appendChild(chip)

      if (label.assigned) {
        const check = document.createElement("span")
        check.className = "text-brand text-[13px] font-semibold"
        check.textContent = "✓"
        row.appendChild(check)
      }

      const pencil = document.createElement("button")
      pencil.type = "button"
      pencil.className = "ml-auto p-1 text-gray-300 hover:text-brand cursor-pointer"
      pencil.title = "Edit label"
      pencil.innerHTML = '<svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor"><path d="M13.586 3.586a2 2 0 112.828 2.828l-8.793 8.793-3.535.707.707-3.535 8.793-8.793z"/></svg>'
      pencil.addEventListener("click", (e) => {
        e.stopPropagation()
        this._hideLabelDropdown()
        this.editLabel(label.id)
      })
      row.appendChild(pencil)
      dd.appendChild(row)
    })

    const exact = (this._labels || []).some(l => l.name.toLowerCase() === query)
    if (raw && !exact) {
      const create = document.createElement("div")
      create.className = "flex items-center gap-1.5 px-2.5 py-1.5 text-[13px] text-brand hover:bg-blue-50 cursor-pointer" +
        (matches.length ? " border-t border-gray-100" : "")
      create.textContent = `+ Create "${raw}"`
      create.addEventListener("click", () => this._startLabelCreate(raw))
      dd.appendChild(create)
    }
    if (!matches.length && !raw) {
      const empty = document.createElement("div")
      empty.className = "px-2.5 py-1.5 text-[13px] text-gray-400"
      empty.textContent = "No labels yet — type a name to create one."
      dd.appendChild(empty)
    }
  }

  // "+ Create" opens the inline editor with the typed name filled in
  // and the next palette color pre-picked, so the person chooses the
  // color before saving (Phelipe 2026-08-26). Save creates AND assigns.
  _startLabelCreate(name) {
    this._hideLabelDropdown()
    this._openLabelEditor(null)
    const colors = (this._data && this._data.label_colors) || []
    if (colors.length) {
      this._editorColor = colors[(this._labels || []).length % colors.length].key
      this._renderLabelSwatches()
    }
    this.labelNameInputTarget.value = name
  }

  async _toggleLabelId(id) {
    if (!this._labels) return
    const previous = this._labels
    const next = previous.map(l => (l.id === id ? { ...l, assigned: !l.assigned } : l))
    this._renderLabels(next) // optimistic — the click should feel instant

    const resp = await this._json("PATCH", this._url(this.showUrlValue) + "/labels", {
      label_ids: next.filter(l => l.assigned).map(l => l.id)
    })
    if (resp && resp.labels) {
      this._renderLabels(resp.labels)
      if (this._data) this._data.labels = resp.labels
    } else {
      this._renderLabels(previous) // server said no — put it back
    }
  }

  // ── Label editing from the popup (Luma 2026-08-26) ────────────────

  editLabel(labelId) {
    const label = (this._labels || []).find(l => l.id === labelId)
    if (label) this._openLabelEditor(label)
  }

  _openLabelEditor(label) {
    if (!this.hasLabelEditorTarget) return
    this._editingLabelId = label ? label.id : null
    this._editorColor = label ? label.color : "gray"
    this._renderLabelSwatches()
    this.labelNameInputTarget.value = label ? label.name : ""
    this.labelDeleteButtonTarget.classList.toggle("hidden", !label)
    this._disarmLabelDelete()
    this.labelEditorTarget.classList.remove("hidden")
    this.labelNameInputTarget.focus()
  }

  _closeLabelEditor() {
    if (!this.hasLabelEditorTarget) return
    this._editingLabelId = null
    this.labelEditorTarget.classList.add("hidden")
    this._disarmLabelDelete()
  }

  cancelLabelEdit(event) {
    event?.preventDefault()
    this._closeLabelEditor()
  }

  // The server hands the palette over with the card payload
  // (label_colors) so the swatch row always matches BoardLabel::COLORS.
  _renderLabelSwatches() {
    this.labelSwatchesTarget.innerHTML = ""
    const colors = (this._data && this._data.label_colors) || []
    colors.forEach(color => {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.title = color.label
      btn.className = `w-5 h-5 rounded cursor-pointer ${color.swatch_class} ` +
        (color.key === this._editorColor ? "ring-2 ring-offset-1 ring-gray-500" : "hover:ring-2 hover:ring-offset-1 hover:ring-gray-300")
      btn.addEventListener("click", (e) => {
        e.preventDefault()
        this._editorColor = color.key
        this._renderLabelSwatches()
      })
      this.labelSwatchesTarget.appendChild(btn)
    })
  }

  async saveLabel(event) {
    event?.preventDefault()
    const name = this.labelNameInputTarget.value.trim()
    if (!name) { this.labelNameInputTarget.focus(); return }

    const base = this._url(this.showUrlValue) + "/labels"
    const resp = this._editingLabelId
      ? await this._json("PATCH", `${base}/${this._editingLabelId}`, { name, color: this._editorColor })
      : await this._json("POST", base, { name, color: this._editorColor })
    if (resp && resp.labels) {
      this._renderLabels(resp.labels)
      if (this._data) this._data.labels = resp.labels
      this._closeLabelEditor()
      if (this.hasLabelSearchInputTarget) this.labelSearchInputTarget.value = ""
    }
  }

  // Two-click delete, no browser confirm (app convention): first click
  // arms red, second within 2.5s deletes the label everywhere.
  async deleteLabel(event) {
    event?.preventDefault()
    if (!this._editingLabelId) return
    const btn = this.labelDeleteButtonTarget

    if (!btn.dataset.armed) {
      btn.dataset.armed = "1"
      btn.classList.add("text-red-600", "font-medium")
      btn.textContent = "Click again to delete it everywhere"
      this._labelDeleteTimer = setTimeout(() => this._disarmLabelDelete(), 2500)
      return
    }

    const resp = await this._json("DELETE",
      this._url(this.showUrlValue) + `/labels/${this._editingLabelId}`)
    if (resp && resp.labels) {
      this._closeLabelEditor()
      this._renderLabels(resp.labels)
      if (this._data) this._data.labels = resp.labels
    }
  }

  _disarmLabelDelete() {
    if (this._labelDeleteTimer) { clearTimeout(this._labelDeleteTimer); this._labelDeleteTimer = null }
    if (!this.hasLabelDeleteButtonTarget) return
    const btn = this.labelDeleteButtonTarget
    delete btn.dataset.armed
    btn.classList.remove("text-red-600", "font-medium")
    btn.textContent = "Delete this label from every project…"
  }

  _renderChecklist(items) {
    this.checklistListTarget.innerHTML = ""
    items.forEach(item => this.checklistListTarget.appendChild(this._checklistRow(item)))
    this._updateChecklistCount(items.filter(i => i.done).length, items.length)
    this._mountChecklistSortable()
  }

  // Trello-style rows: drag handle · checkbox · click-to-rename label · ×
  _checklistRow(item) {
    const row = document.createElement("div")
    row.className = "flex items-start gap-1.5 py-0.5 group rounded hover:bg-gray-50"
    row.dataset.itemId = item.id

    const handle = document.createElement("span")
    handle.className = "mt-0.5 px-0.5 text-gray-300 hover:text-gray-500 cursor-grab opacity-0 group-hover:opacity-100 transition-opacity"
    handle.setAttribute("data-checklist-drag", "")
    handle.innerHTML = '<svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor"><circle cx="2.5" cy="3" r="1.3"/><circle cx="7.5" cy="3" r="1.3"/><circle cx="2.5" cy="8" r="1.3"/><circle cx="7.5" cy="8" r="1.3"/><circle cx="2.5" cy="13" r="1.3"/><circle cx="7.5" cy="13" r="1.3"/></svg>'

    const box = document.createElement("input")
    box.type = "checkbox"
    box.checked = !!item.done
    box.className = "mt-0.5 rounded border-gray-300 cursor-pointer shrink-0"
    box.addEventListener("change", () => this._toggleItem(item.id, box))

    const text = document.createElement("span")
    text.className = "flex-1 min-w-0"
    const label = document.createElement("span")
    label.className = `block text-[13px] cursor-text ${item.done ? "text-gray-400 line-through" : "text-gray-700"}`
    label.textContent = item.label
    label.title = "Click to edit"
    label.addEventListener("click", () => this._editChecklistLabel(item, label))
    text.appendChild(label)
    if (item.done && item.done_by) {
      const meta = document.createElement("span")
      meta.className = "block text-[11px] text-gray-400"
      meta.textContent = `${item.done_by} · ${item.done_at || ""}`
      text.appendChild(meta)
    }

    const del = document.createElement("button")
    del.type = "button"
    del.className = "mt-0.5 w-4 h-4 shrink-0 text-gray-300 hover:text-red-600 cursor-pointer leading-none opacity-0 group-hover:opacity-100 transition-opacity"
    del.textContent = "×"
    del.addEventListener("click", () => this._deleteChecklistItem(item.id, row))

    row.appendChild(handle)
    row.appendChild(box)
    row.appendChild(text)
    row.appendChild(del)
    return row
  }

  _mountChecklistSortable() {
    if (this._checklistSortable) this._checklistSortable.destroy()
    this._checklistSortable = Sortable.create(this.checklistListTarget, {
      handle: "[data-checklist-drag]",
      animation: 150,
      onEnd: () => this._saveChecklistOrder()
    })
  }

  async addChecklistItem(event) {
    event?.preventDefault()
    const label = this.checklistNewInputTarget.value.trim()
    if (!label) return

    const resp = await this._json("POST", this._url(this.showUrlValue) + "/checklist", { label })
    if (!resp) return

    this.checklistNewInputTarget.value = ""
    this._data.checklist = [...(this._data.checklist || []), resp.item]
    this.checklistListTarget.appendChild(this._checklistRow(resp.item))
    this._updateChecklistCount(resp.counts.done, resp.counts.total)
    this._updateCardCounter("checklist", `${resp.counts.done}/${resp.counts.total}`)
    this.checklistNewInputTarget.focus()
  }

  _editChecklistLabel(item, labelEl) {
    const input = document.createElement("input")
    input.type = "text"
    input.value = item.label
    input.className = "w-full px-2 py-1 text-[13px] border border-brand rounded bg-white focus:outline-none"
    labelEl.replaceWith(input)
    input.focus()
    input.select()

    let finished = false
    const finish = async (save) => {
      if (finished) return
      finished = true
      const next = input.value.trim()
      if (save && next && next !== item.label) {
        const resp = await this._json("PATCH",
          this._url(this.showUrlValue) + `/checklist/${item.id}/label`, { label: next })
        if (resp) {
          item.label = resp.item.label
          this._data.checklist = (this._data.checklist || []).map(i => (i.id === item.id ? resp.item : i))
        }
      }
      this._renderChecklist(this._data.checklist || [])
    }
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") { e.preventDefault(); finish(true) }
      if (e.key === "Escape") { e.preventDefault(); finish(false) }
    })
    input.addEventListener("blur", () => finish(true))
  }

  async _deleteChecklistItem(id, row) {
    const resp = await this._json("DELETE", this._url(this.showUrlValue) + `/checklist/${id}`)
    if (!resp) return

    row.remove()
    this._data.checklist = (this._data.checklist || []).filter(i => i.id !== id)
    this._updateChecklistCount(resp.counts.done, resp.counts.total)
    this._updateCardCounter("checklist", `${resp.counts.done}/${resp.counts.total}`)
  }

  async _saveChecklistOrder() {
    const ids = Array.from(this.checklistListTarget.children).map(el => parseInt(el.dataset.itemId, 10))
    await this._json("PATCH", this._url(this.showUrlValue) + "/checklist/reorder", { ids })
    const byId = Object.fromEntries((this._data.checklist || []).map(i => [i.id, i]))
    this._data.checklist = ids.map(id => byId[id]).filter(Boolean)
  }

  _renderComments(comments) {
    this.commentsListTarget.innerHTML = ""
    // Server sends newest first; the Task chat pane reads like a chat —
    // oldest at the top, latest at the bottom, scrolled into view.
    ;[...comments].reverse().forEach(c => this.commentsListTarget.appendChild(this._commentRow(c)))
    this._scrollChatToBottom()
  }

  _scrollChatToBottom() {
    requestAnimationFrame(() => { this.commentsListTarget.scrollTop = this.commentsListTarget.scrollHeight })
  }

  // Enter posts, Shift+Enter keeps the newline — chat conventions.
  commentEnter(event) {
    if (event.shiftKey) return
    event.preventDefault()
    this.addComment(event)
  }

  _commentRow(comment) {
    const row = document.createElement("div")
    // Your own updates read blue, everyone else's gray — the thread
    // scans like a chat instead of a uniform list.
    row.className = comment.mine
      ? "rounded-lg bg-blue-50 border border-blue-100 px-3 py-2"
      : "rounded-lg bg-gray-50 border border-gray-100 px-3 py-2"
    row.dataset.commentId = comment.id

    const head = document.createElement("div")
    head.className = "flex items-center justify-between gap-2"
    const who = document.createElement("p")
    who.className = "text-[11px] text-gray-500 font-medium"
    who.textContent = `${comment.user_name} · ${comment.created_at}`
    head.appendChild(who)
    if (comment.mine) {
      const del = document.createElement("button")
      del.type = "button"
      del.className = "text-[11px] text-gray-400 hover:text-red-600 cursor-pointer"
      del.textContent = "×"
      del.addEventListener("click", () => this._deleteComment(comment.id, row))
      head.appendChild(del)
    }

    const body = document.createElement("p")
    body.className = "text-[13px] text-gray-700 whitespace-pre-wrap break-words"
    body.textContent = comment.body

    row.appendChild(head)
    row.appendChild(body)
    return row
  }

  _renderAttachments(attachments) {
    this.attachmentsListTarget.innerHTML = ""
    attachments.forEach(a => this.attachmentsListTarget.appendChild(this._attachmentChip(a)))
  }

  _attachmentChip(attachment) {
    const chip = document.createElement("span")
    chip.className = "inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full bg-gray-100 border border-gray-200 text-[13px] text-gray-700 max-w-full"
    chip.dataset.attachmentId = attachment.id

    const link = document.createElement("a")
    link.href = attachment.url
    link.target = "_blank"
    link.rel = "noopener"
    link.className = "truncate max-w-[220px] hover:text-brand"
    link.title = attachment.uploaded_by ? `${attachment.file_name} · ${attachment.uploaded_by}` : attachment.file_name
    link.textContent = attachment.file_name
    chip.appendChild(link)

    if (attachment.mine || attachment.can_delete) {
      const del = document.createElement("button")
      del.type = "button"
      del.className = "w-4 h-4 rounded-full text-gray-400 hover:text-red-600 cursor-pointer leading-none"
      del.textContent = "×"
      del.addEventListener("click", () => this._deleteAttachment(attachment.id, chip))
      chip.appendChild(del)
    }
    return chip
  }

  // ── Description editing ──────────────────────────────────────────

  editDescription(event) {
    event?.preventDefault()
    this.descriptionInputTarget.value = this._data.description || ""
    this.descriptionTextTarget.classList.add("hidden")
    this.descriptionEditButtonTarget.classList.add("hidden")
    this.descriptionInputTarget.classList.remove("hidden")
    this.descriptionSaveButtonTarget.classList.remove("hidden")
    this.descriptionCancelButtonTarget.classList.remove("hidden")
    this.descriptionInputTarget.focus()
  }

  cancelDescription(event) {
    event?.preventDefault()
    this._renderDescription()
  }

  async saveDescription(event) {
    event?.preventDefault()
    const resp = await this._json("PATCH", this._url(this.showUrlValue) + "/description",
      { description: this.descriptionInputTarget.value })
    if (resp) {
      this._data.description = resp.description
      this._renderDescription()
    }
  }

  // ── Checklist ────────────────────────────────────────────────────

  async _toggleItem(itemId, box) {
    const resp = await this._json("PATCH",
      this._url(this.showUrlValue) + `/checklist/${itemId}`, { done: box.checked })
    if (!resp) { box.checked = !box.checked; return }

    const items = (this._data.checklist || []).map(i => (i.id === resp.item.id ? resp.item : i))
    this._data.checklist = items
    this._renderChecklist(items)
    this._updateCardCounter("checklist", `${resp.counts.done}/${resp.counts.total}`)
  }

  _updateChecklistCount(done, total) {
    this.checklistCountTarget.textContent = `${done}/${total}`
    if (this.hasChecklistBarTarget) {
      const pct = total > 0 ? Math.round((done / total) * 100) : 0
      this.checklistBarTarget.style.width = `${pct}%`
    }
  }

  // ── Comments ─────────────────────────────────────────────────────

  async addComment(event) {
    event?.preventDefault()
    const body = this.commentInputTarget.value.trim()
    if (!body) return

    const resp = await this._json("POST", this._url(this.showUrlValue) + "/comments", { body })
    if (!resp) return

    this.commentInputTarget.value = ""
    this._data.comments = [resp.comment, ...(this._data.comments || [])]
    this.commentsListTarget.appendChild(this._commentRow(resp.comment))
    this._scrollChatToBottom()
    this._bumpCardCounter("comments", +1)
  }

  async _deleteComment(id, row) {
    const resp = await this._json("DELETE", this._url(this.showUrlValue) + `/comments/${id}`)
    if (!resp) return
    row.remove()
    this._data.comments = (this._data.comments || []).filter(c => c.id !== id)
    this._bumpCardCounter("comments", -1)
  }

  // ── Attachments (presign → PUT → register) ───────────────────────

  browse(event) {
    event?.preventDefault()
    this.fileInputTarget.click()
  }

  filesChosen(event) {
    const files = Array.from(event.target?.files || [])
    event.target.value = ""
    files.forEach(f => this._upload(f))
  }

  dragOver(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.add("border-brand", "bg-blue-50/30")
  }

  dragLeave() {
    this.dropzoneTarget.classList.remove("border-brand", "bg-blue-50/30")
  }

  drop(event) {
    event.preventDefault()
    this.dragLeave()
    Array.from(event.dataTransfer?.files || []).forEach(f => this._upload(f))
  }

  async _upload(file) {
    // Blue chip with spinner while uploading, per the app's pattern.
    const chip = document.createElement("span")
    chip.className = "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 border border-blue-200 text-[13px] text-blue-700 max-w-full"
    chip.innerHTML = `<svg class="animate-spin h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg><span class="truncate max-w-[220px]">${this._escape(file.name)}</span>`
    this.attachmentsListTarget.prepend(chip)

    try {
      const presign = await this._json("POST",
        this._url(this.showUrlValue) + "/attachments/presign",
        { file_name: file.name, byte_size: file.size })
      if (!presign) throw new Error("presign failed")

      const put = await fetch(presign.url, { method: "PUT", body: file })
      if (!put.ok) throw new Error(`S3 ${put.status}`)

      const resp = await this._json("POST", this._url(this.showUrlValue) + "/attachments",
        { file_name: file.name, s3_key: presign.s3_key, byte_size: file.size })
      if (!resp) throw new Error("register failed")

      chip.replaceWith(this._attachmentChip(resp.attachment))
      this._data.attachments = [resp.attachment, ...(this._data.attachments || [])]
      this._bumpCardCounter("attachments", +1)
    } catch (err) {
      chip.remove()
      console.warn("[board-card] upload failed", err)
    }
  }

  async _deleteAttachment(id, chip) {
    const resp = await this._json("DELETE", this._url(this.showUrlValue) + `/attachments/${id}`)
    if (!resp) return
    chip.remove()
    this._data.attachments = (this._data.attachments || []).filter(a => a.id !== id)
    this._bumpCardCounter("attachments", -1)
  }

  // ── Card counter sync ────────────────────────────────────────────

  _counterEl(kind) {
    return this.element.querySelector(
      `[data-board-card-counters="${this._projectId}"] [data-counter="${kind}"] [data-counter-value]`)
  }

  _updateCardCounter(kind, text) {
    const el = this._counterEl(kind)
    if (el) el.textContent = text
  }

  _bumpCardCounter(kind, delta) {
    const el = this._counterEl(kind)
    if (!el) return
    const next = Math.max(0, (parseInt(el.textContent, 10) || 0) + delta)
    el.textContent = String(next)
  }

  // ── Utils ────────────────────────────────────────────────────────

  _showLoading(on) {
    this.loadingTarget.classList.toggle("hidden", !on)
  }

  _hideSections() {
    ;[this.descriptionSectionTarget, this.checklistSectionTarget,
      this.commentsSectionTarget, this.attachmentsSectionTarget,
      this.labelsSectionTarget]
      .forEach(el => el.classList.add("hidden"))
  }

  _url(template) {
    return template.replace(":project_id", this._projectId)
  }

  async _json(method, url, payload = null) {
    try {
      const res = await fetch(url, {
        method,
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfTokenValue || (document.querySelector('meta[name="csrf-token"]')?.content || "")
        },
        credentials: "same-origin",
        body: payload ? JSON.stringify(payload) : null
      })
      const body = await res.json().catch(() => null)
      if (!res.ok) {
        console.warn("[board-card] request failed", url, body)
        return null
      }
      return body
    } catch (err) {
      console.warn("[board-card] network error", err)
      return null
    }
  }

  _escape(str) {
    return String(str || "").replace(/[&<>"']/g, (c) => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
    }[c]))
  }
}
